// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngCarousel from "@eisinc/App/Components/XngCarousel";
import loadPurchasedProducts from "@eisinc/App/Handlers/LoadPurchasedProducts";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@eisinc/Overrides/Components/ProductCarouselProduct";
import { CarouselCardType } from "@eisinc/XDL/types";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import PurchasedProductCard from "@insite/content-library/Components/PurchasedProductCard";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    title = "title",
    titlePosition = "titlePosition",
    purchaseType = "purchaseType",
    showBrand = "showBrand",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAvailability = "showAvailability",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    carouselTemplate = "carouselTemplate",
    // XNG-Changes
    view = "view",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titlePosition]: string;
        [fields.purchaseType]: string;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.carouselTemplate]: string;
        // XNG-Changes
        [fields.view]: CarouselCardType;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    return {
        products: getProductsForProductInfoList(state, ownProps.id),
        canViewPurchasedProducts: isAuthenticated,
        // XNG-Changes
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = {
    loadPurchasedProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PurchasedProductsStyles {
    titleWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    headerContainer?: GridContainerProps;
    itemHeaderGridItem?: GridItemProps;
    itemHeaderText?: TypographyPresentationProps;
    priceHeaderGridItem?: GridItemProps;
    priceHeaderText?: TypographyPresentationProps;
    addToCartHeaderGridItem?: GridItemProps;
    addToCartHeaderText?: TypographyPresentationProps;
    // XNG-Changes
    borderSeparator?: InjectableCss;
    productDescriptionHeaderGridItem?: GridItemProps;
    imageHeaderStyledWrapper?: InjectableCss;
    availabilityHeaderGridItem?: GridItemProps;
    availabilityHeaderText?: TypographyPresentationProps;
    scrollProductsContainer?: InjectableCss;
    carouselProductCondensedStyles?: ProductCarouselProductStyles;
    miniCardStyles?: ProductCarouselProductStyles;
    carouselProductStyles?: ProductCarouselProductStyles;
}

export const purchasedProductsStyles: PurchasedProductsStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    headerContainer: {
        gap: 0,
        css: css`
            padding: 10px;
            background-color: ${getColor("common.accent")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    itemHeaderGridItem: { width: [0, 0, 6, 6, 6] },
    itemHeaderText: { weight: "bold" },
    priceHeaderGridItem: {
        width: [0, 0, 3, 3, 3],
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding-right: 35px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    priceHeaderText: { weight: "bold" },
    addToCartHeaderGridItem: {
        width: [0, 0, 3, 3, 3],
        css: css`
            justify-content: flex-end;
        `,
    },
    addToCartHeaderText: { weight: "bold" },
};

const styles = purchasedProductsStyles;

const getJustifyContent = (position: string) => {
    if (position === "left") {
        return "flex-start";
    }
    if (position === "right") {
        return "flex-end";
    }
    return "center";
};

const TitleWrapper = styled.div<{ position: string }>`
    display: flex;
    justify-content: ${({ position }) => getJustifyContent(position)};
`;

// EIS-4192 - replace div with semantic HTML elements
const SectionWrapper = styled.section``;

const PurchasedProducts = ({
    id,
    fields,
    products,
    canViewPurchasedProducts,
    loadPurchasedProducts,
    // XNG-Changes
    isTabletScreen,
}: Props) => {
    React.useEffect(() => {
        if (canViewPurchasedProducts) {
            loadPurchasedProducts({ widgetId: id, purchaseType: fields.purchaseType });
        }
    }, [fields.purchaseType]);

    if (!canViewPurchasedProducts || !products || products.length === 0) {
        return null;
    }

    // XNG-Changes
    const carouselCardStyles =
        fields.view === "condensed"
            ? styles.carouselProductCondensedStyles
            : fields.view === "miniCard" || (isTabletScreen && fields.view === "grid")
            ? styles.miniCardStyles
            : styles.carouselProductStyles;

    return (
        // xng-change - adding aria labelledby for accessibility
        <SectionWrapper aria-labelledby={translate("Purchased Products")}>
            <TitleWrapper {...styles.titleWrapper} position={fields.titlePosition}>
                <Typography {...styles.titleText}>{fields.title}</Typography>
            </TitleWrapper>
            <StyledWrapper
                {...styles.borderSeparator}
                {...{ carouselTemplate: fields.carouselTemplate }}
            ></StyledWrapper>
            {fields.carouselTemplate === "table" && (
                <StyledWrapper {...styles.headerContainer}>
                    <StyledWrapper {...styles.imageHeaderStyledWrapper} />
                    <StyledWrapper {...styles.productDescriptionHeaderGridItem}>
                        <Typography {...styles.itemHeaderText}>{translate("Product Description")}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.priceHeaderGridItem}>
                        <Typography {...styles.priceHeaderText}>{translate("Price")}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.availabilityHeaderGridItem}>
                        <Typography {...styles.availabilityHeaderText}>{translate("Availability")}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.addToCartHeaderGridItem}>
                        <Typography {...styles.addToCartHeaderText}></Typography>
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {fields.carouselTemplate === "carousel" && (
                <XngCarousel
                    id={id}
                    navigation={true}
                    spaceBetween={fields.view === "condensed" ? 24 : 22}
                    scrollbar={true}
                    shadowWidth="64px"
                    showArrowsOnMobile={false}
                >
                    {products.map((product, index) => (
                        <React.Fragment key={product.id}>
                            <ProductCarouselProduct
                                carouselId={id}
                                product={product}
                                showImage={fields.showImage}
                                showBrand={fields.showBrand && fields.view !== "condensed"}
                                showTitle={fields.showTitle}
                                showPartNumbers={fields.showPartNumbers && fields.view !== "condensed"}
                                showPrice={fields.showPrice}
                                showAddToCart={fields.showAddToCart}
                                showAddToList={fields.showAddToList && fields.view !== "condensed"}
                                showImageBadges={fields.showImageBadges}
                                maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                showTextBadges={fields.showTextBadges}
                                maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                                view={isTabletScreen && fields.view === "grid" ? "miniCard" : fields.view}
                                showAvailability={fields.showAvailability}
                                extendedStyles={carouselCardStyles}
                            />
                        </React.Fragment>
                    ))}
                </XngCarousel>
            )}
            {fields.carouselTemplate === "table" && (
                <StyledWrapper {...styles.scrollProductsContainer}>
                    {products.map(product => (
                        <PurchasedProductCard
                            key={product.id}
                            product={product}
                            widgetId={id}
                            showBrand={fields.showBrand}
                            showPartNumbers={fields.showPartNumbers}
                            showPrice={fields.showPrice}
                            showAvailability={fields.showAvailability}
                            showAddToCart={fields.showAddToCart}
                            showAddToList={fields.showAddToList}
                            extendedStyles={{
                                container: {
                                    css: css`
                                        padding: 10px;
                                        border-bottom: 1px solid ${getColor("common.ui7")};
                                        min-width: 700px;
                                    `,
                                },
                            }}
                        />
                    ))}
                </StyledWrapper>
            )}
        </SectionWrapper>
    );
};

const carouselTemplateOptions = [
    { value: "table", displayName: "Table" },
    { value: "carousel", displayName: "Carousel" },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PurchasedProducts),
    definition: {
        group: "Common",
        icon: "list-ul",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.titlePosition,
                displayName: "Title Position",
                editorTemplate: "DropDownField",
                options: [
                    { value: "center", displayName: "Center" },
                    { value: "left", displayName: "Left" },
                    { value: "right", displayName: "Right" },
                ],
                defaultValue: "center",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.purchaseType,
                displayName: "Purchase Type",
                editorTemplate: "DropDownField",
                options: [
                    { value: "recently", displayName: "Recently Purchased" },
                    { value: "frequently", displayName: "Frequently Purchased" },
                ],
                defaultValue: "recently",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.carouselTemplate,
                displayName: "Carousel Template",
                editorTemplate: "DropDownField",
                options: carouselTemplateOptions,
                defaultValue: "carousel",
                fieldType: "General",
                sortOrder: 3,
                isRequired: true,
            },
            {
                name: fields.view,
                displayName: "Card Type",
                editorTemplate: "RadioButtonsField",
                defaultValue: "grid",
                horizontal: true,
                fieldType: "General",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Grid",
                        value: "grid",
                    },
                    {
                        displayName: "Condensed",
                        value: "miniCard",
                    },
                    {
                        displayName: "Thumbnail",
                        value: "condensed",
                    },
                ],
                isVisible: item => item.fields[fields.carouselTemplate] === "carousel",
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
                sortOrder: 5,
                isVisible: item => item.fields[fields.carouselTemplate] === "carousel",
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
                sortOrder: 6,
                isVisible: item => item.fields[fields.carouselTemplate] === "carousel",
            },
            {
                name: fields.showBrand,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add To Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 11,
            },
            {
                name: fields.showAddToList,
                displayName: "Add To List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 12,
            },
            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselTemplate] === "carousel",
                sortOrder: 13,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                sortOrder: 14,
                fieldType: "Contextual",
            },
            {
                name: fields.showTextBadges,
                displayName: "Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 15,
                isVisible: item => item.fields[fields.carouselTemplate] === "carousel",
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                sortOrder: 16,
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
